import { useQuery } from '@tanstack/react-query';

import type {
  AnalysisData,
  SectionItem,
  SeparationResult,
  SharedStudioState,
  TabDetail,
  TabTimingMap,
  WaveformData,
} from '@/features/separation/api';
import { withTokenStemColors } from '@/features/separation/api';

import { parsePublicPracticeSongValue } from './bootstrap';
import {
  parsePublicPracticeArtistValue,
  parsePublicPracticeCatalogValue,
} from './catalog-bootstrap';
import type {
  PublicPracticeArtist,
  PublicPracticeCatalog,
  PublicPracticeRight,
  PublicPracticeSong,
} from './types';

const API_SONG_KEYS = [
  'song_key',
  'slug',
  'language',
  'title',
  'artist_name',
  'artist_slug',
  'artist_schema_type',
  'release_year',
  'genre',
  'difficulty',
  'vocal_range',
  'version_label',
  'composer',
  'seo_title',
  'meta_description',
  'hero_summary',
  'introduction',
  'bpm',
  'musical_key',
  'duration_sec',
  'practice_kind',
  'sections',
  'preset',
  'practice_steps',
  'limitations',
  'credits',
  'rights',
  'allowed_stems',
  'mr_path',
  'hub_path',
  'room_path',
  'audio_qa_at',
  'editorial_qa_at',
  'published_at',
] as const;

export class PublicPracticeApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'PublicPracticeApiError';
  }
}

export interface PublicPracticeDetail
  extends Omit<SeparationResult, 'id'> {
  id?: never;
}

export interface PublicPracticeRoomMetadata extends PublicPracticeSong {
  preset: SharedStudioState;
  allowed_stems: string[];
}

export const publicPracticeQk = {
  all: ['public-practice'] as const,
  catalog: ['public-practice', 'catalog'] as const,
  artist: (slug: string) => [
    ...publicPracticeQk.all,
    'artist',
    slug,
  ] as const,
  detail: (slug: string) => [...publicPracticeQk.all, slug, 'detail'] as const,
  analysis: (slug: string) => [...publicPracticeQk.all, slug, 'analysis'] as const,
  sections: (slug: string) => [...publicPracticeQk.all, slug, 'sections'] as const,
  tabs: (slug: string) => [...publicPracticeQk.all, slug, 'tabs'] as const,
};

async function fetchPublicPracticeDiscoveryJson(
  path: string,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await fetch(path, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new PublicPracticeApiError(
      response.status,
      response.status === 404
        ? '공개 연습 목록을 찾을 수 없습니다.'
        : '공개 연습 목록을 준비할 수 없습니다.',
    );
  }
  try {
    return await response.json() as unknown;
  } catch {
    throw new PublicPracticeApiError(
      502,
      '공개 연습 목록 응답을 읽을 수 없습니다.',
    );
  }
}

export async function fetchPublicPracticeCatalog(
  signal?: AbortSignal,
): Promise<PublicPracticeCatalog> {
  const payload = await fetchPublicPracticeDiscoveryJson(
    '/api/public-practice-songs/catalog',
    signal,
  );
  const catalog = parsePublicPracticeCatalogValue(payload);
  if (catalog === null) {
    throw new PublicPracticeApiError(
      502,
      '공개 연습 목록 응답 형식이 올바르지 않습니다.',
    );
  }
  return catalog;
}

export async function fetchPublicPracticeArtist(
  slug: string,
  signal?: AbortSignal,
): Promise<PublicPracticeArtist> {
  const payload = await fetchPublicPracticeDiscoveryJson(
    `/api/public-practice-songs/artists/${encodeURIComponent(slug)}`,
    signal,
  );
  const artist = parsePublicPracticeArtistValue(payload);
  if (artist === null || artist.slug !== slug) {
    throw new PublicPracticeApiError(
      502,
      '공개 아티스트 응답 형식이 올바르지 않습니다.',
    );
  }
  return artist;
}

export function usePublicPracticeCatalog(
  initialData: PublicPracticeCatalog | null,
  options?: { enabled?: boolean },
) {
  return useQuery<PublicPracticeCatalog, PublicPracticeApiError>({
    queryKey: publicPracticeQk.catalog,
    queryFn: ({ signal }) => fetchPublicPracticeCatalog(signal),
    enabled: options?.enabled ?? true,
    initialData: initialData ?? undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
}

export function usePublicPracticeArtist(
  slug: string | null,
  initialData: PublicPracticeArtist | null,
  options?: { enabled?: boolean },
) {
  return useQuery<PublicPracticeArtist, PublicPracticeApiError>({
    queryKey: slug
      ? publicPracticeQk.artist(slug)
      : [...publicPracticeQk.all, 'artist', 'none'],
    queryFn: ({ signal }) => fetchPublicPracticeArtist(slug as string, signal),
    enabled: (options?.enabled ?? true) && Boolean(slug),
    initialData: initialData ?? undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
}

function publicPath(slug: string, suffix: string): string {
  return `/api/public-practice-songs/${encodeURIComponent(slug)}/${suffix}`;
}

async function fetchPublicJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new PublicPracticeApiError(
      response.status,
      response.status === 404
        ? '공개 연습 자료를 찾을 수 없습니다.'
        : '공개 연습 자료를 준비할 수 없습니다.',
    );
  }
  return response.json() as Promise<T>;
}

export function fetchPublicPracticeDetail(
  slug: string,
): Promise<PublicPracticeDetail> {
  return fetchPublicJson<PublicPracticeDetail>(publicPath(slug, 'detail'));
}

export function fetchPublicPracticeAnalysis(
  slug: string,
): Promise<AnalysisData> {
  return fetchPublicJson<AnalysisData>(publicPath(slug, 'analysis'));
}

export function fetchPublicPracticeSections(
  slug: string,
): Promise<SectionItem[]> {
  return fetchPublicJson<SectionItem[]>(publicPath(slug, 'sections'));
}

export function fetchPublicPracticeTabs(slug: string): Promise<TabDetail[]> {
  return fetchPublicJson<TabDetail[]>(publicPath(slug, 'tabs'));
}

export function fetchPublicPracticeWaveform(
  slug: string,
  stemName: string,
): Promise<WaveformData> {
  return fetchPublicJson<WaveformData>(
    publicPath(slug, `waveform/${encodeURIComponent(stemName)}`),
  );
}

export function fetchPublicPracticeTab(
  slug: string,
  tabId: number,
): Promise<TabDetail> {
  return fetchPublicJson<TabDetail>(
    publicPath(slug, `tab/${encodeURIComponent(String(tabId))}`),
  );
}

export function fetchPublicPracticeTabTiming(
  slug: string,
  tabId: number,
): Promise<TabTimingMap> {
  return fetchPublicJson<TabTimingMap>(
    publicPath(
      slug,
      `tab/${encodeURIComponent(String(tabId))}/timing`,
    ),
  );
}

export function publicPracticeStemUrl(
  slug: string,
  stemName: string,
): string {
  return publicPath(slug, `stems/${encodeURIComponent(stemName)}`);
}

export function usePublicPracticeDetail(
  slug: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery<SeparationResult>({
    queryKey: slug
      ? publicPracticeQk.detail(slug)
      : [...publicPracticeQk.all, 'none', 'detail'],
    queryFn: async () => {
      const detail = await fetchPublicPracticeDetail(slug as string);
      return withTokenStemColors({
        ...detail,
        id: 0,
      } as SeparationResult);
    },
    enabled: (options?.enabled ?? true) && Boolean(slug),
    retry: false,
  });
}

export function usePublicPracticeAnalysis(
  slug: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery<AnalysisData>({
    queryKey: slug
      ? publicPracticeQk.analysis(slug)
      : [...publicPracticeQk.all, 'none', 'analysis'],
    queryFn: () => fetchPublicPracticeAnalysis(slug as string),
    enabled: (options?.enabled ?? true) && Boolean(slug),
    retry: false,
  });
}

export function usePublicPracticeSections(
  slug: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery<SectionItem[]>({
    queryKey: slug
      ? publicPracticeQk.sections(slug)
      : [...publicPracticeQk.all, 'none', 'sections'],
    queryFn: () => fetchPublicPracticeSections(slug as string),
    enabled: (options?.enabled ?? true) && Boolean(slug),
    retry: false,
  });
}

export function usePublicPracticeTabs(
  slug: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery<TabDetail[]>({
    queryKey: slug
      ? publicPracticeQk.tabs(slug)
      : [...publicPracticeQk.all, 'none', 'tabs'],
    queryFn: () => fetchPublicPracticeTabs(slug as string),
    enabled: (options?.enabled ?? true) && Boolean(slug),
    retry: false,
  });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && actual.every((key) => keys.includes(key));
}

function projectRights(value: unknown): PublicPracticeRight[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const rights: PublicPracticeRight[] = [];
  for (const item of value) {
    if (
      !isObject(item)
      || !hasExactKeys(item, [
        'layer',
        'status',
        'basis',
        'source_url',
        'reviewed_at',
      ])
    ) {
      return null;
    }
    rights.push({
      layer: item.layer as string,
      status: item.status as string,
      basis: item.basis as string,
      source_url: item.source_url as string,
      reviewed_at: item.reviewed_at as string,
    });
  }
  return rights;
}

function isSharedStudioState(value: unknown): value is SharedStudioState {
  if (
    !isObject(value)
    || !hasExactKeys(value, [
      'loop_start_sec',
      'loop_end_sec',
      'bpm_ratio',
      'pitch_semitones',
      'selected_tab_id',
      'selected_tab_renderer',
      'track_settings',
    ])
    || !isObject(value.track_settings)
  ) {
    return false;
  }
  const selectedPairIsComplete =
    (value.selected_tab_id === null && value.selected_tab_renderer === null)
    || (
      typeof value.selected_tab_id === 'number'
      && Number.isInteger(value.selected_tab_id)
      && value.selected_tab_id > 0
      && value.selected_tab_renderer === 'alphatab'
    );
  return (
    typeof value.loop_start_sec === 'number'
    && Number.isFinite(value.loop_start_sec)
    && typeof value.loop_end_sec === 'number'
    && Number.isFinite(value.loop_end_sec)
    && typeof value.bpm_ratio === 'number'
    && Number.isFinite(value.bpm_ratio)
    && typeof value.pitch_semitones === 'number'
    && Number.isInteger(value.pitch_semitones)
    && selectedPairIsComplete
    && Object.values(value.track_settings).every((settings) => (
      isObject(settings)
      && hasExactKeys(settings, ['volume', 'pan', 'muted'])
      && typeof settings.volume === 'number'
      && Number.isFinite(settings.volume)
      && typeof settings.pan === 'number'
      && Number.isFinite(settings.pan)
      && typeof settings.muted === 'boolean'
    ))
  );
}

function projectApiRoomMetadata(
  value: unknown,
): PublicPracticeRoomMetadata | null {
  if (!isObject(value) || !hasExactKeys(value, API_SONG_KEYS)) {
    return null;
  }
  if (
    !isSharedStudioState(value.preset)
    || !Array.isArray(value.allowed_stems)
    || !value.allowed_stems.every((stem) => typeof stem === 'string')
  ) {
    return null;
  }
  const rights = projectRights(value.rights);
  if (rights === null) {
    return null;
  }
  const projected = Object.fromEntries(
    Object.entries(value).filter(([key]) => (
      key !== 'preset' && key !== 'allowed_stems' && key !== 'rights'
    )),
  );
  projected.rights = rights;
  const song = parsePublicPracticeSongValue(projected);
  return song === null
    ? null
    : {
      ...song,
      preset: value.preset,
      allowed_stems: [...value.allowed_stems],
    };
}

function projectApiSong(value: unknown): PublicPracticeSong | null {
  const metadata = projectApiRoomMetadata(value);
  if (metadata === null) return null;
  const { preset: _preset, allowed_stems: _allowedStems, ...song } = metadata;
  return song;
}

export async function fetchPublicPracticeSong(
  slug: string,
  signal?: AbortSignal,
): Promise<PublicPracticeSong> {
  const response = await fetch(
    `/api/public-practice-songs/${encodeURIComponent(slug)}`,
    {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal,
    },
  );
  if (!response.ok) {
    throw new PublicPracticeApiError(
      response.status,
      response.status === 404
        ? '공개 연습 곡을 찾을 수 없습니다.'
        : '공개 연습 곡의 MR을 준비할 수 없습니다.',
    );
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new PublicPracticeApiError(502, '공개 연습 곡 응답을 읽을 수 없습니다.');
  }
  const song = projectApiSong(payload);
  if (song === null || song.slug !== slug) {
    throw new PublicPracticeApiError(502, '공개 연습 곡 응답 형식이 올바르지 않습니다.');
  }
  return song;
}

export async function fetchPublicPracticeRoomMetadata(
  slug: string,
  signal?: AbortSignal,
): Promise<PublicPracticeRoomMetadata> {
  const response = await fetch(
    `/api/public-practice-songs/${encodeURIComponent(slug)}`,
    {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal,
    },
  );
  if (!response.ok) {
    throw new PublicPracticeApiError(
      response.status,
      response.status === 404
        ? '공개 연습 곡을 찾을 수 없습니다.'
        : '공개 연습 곡을 준비할 수 없습니다.',
    );
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new PublicPracticeApiError(
      502,
      '공개 연습 곡 응답을 읽을 수 없습니다.',
    );
  }
  const metadata = projectApiRoomMetadata(payload);
  if (metadata === null || metadata.slug !== slug) {
    throw new PublicPracticeApiError(
      502,
      '공개 연습 곡 응답 형식이 올바르지 않습니다.',
    );
  }
  return metadata;
}
